import React, { useEffect, useMemo, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import axios, { readProfileUser } from '../../utils/api';
import { dashboardReturnUrl, portalLoginUrl } from '../../utils/portalLogin';
import {
  extractPreviewGenerationId,
  navigatePreviewWindow,
  openPendingPreviewWindow,
  requestPreviewAccess,
} from '../../utils/previewAccess';
import {
  UiFont,
  UiTheme,
  getSavedUiFont,
  getSavedUiTheme,
  getSystemUiTheme,
  setUiFont,
  setUiTheme,
} from '../../utils/uiTheme';
import AppTopbar from './AppTopbar';
import AdminNavigation from '../admin/AdminNavigation';

// Slug сервиса в portal_services.portal_auth — должен совпадать с записью в БД.
const ACTIVE_SERVICE_SLUG = 'whitepage-generator';

export interface AuthUser {
  id: number;
  email: string;
  role: string;
  balance?: number;
  currency?: 'USD';
  generation_limit?: number;
}

const PORTAL_PROFILE_REVISION_KEY = 'portal:profileRevision';

const getCurrentUiThemeFromDom = (): UiTheme => {
  const raw = document.documentElement.getAttribute('data-ui-theme');
  return raw === 'dark' ? 'dark' : 'light';
};

const getCurrentUiFontFromDom = (): UiFont => {
  const raw = document.documentElement.getAttribute('data-ui-font');
  return raw === 'system' ? 'system' : 'web';
};

const AppLayout: React.FC = () => {
  const location = useLocation();

  const [user, setUser] = useState<AuthUser | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [serviceAccessDenied, setServiceAccessDenied] = useState(false);
  const [uiTheme, setUiThemeState] = useState<UiTheme>(() => {
    if (typeof document === 'undefined') return 'light';
    return getSavedUiTheme() ?? getCurrentUiThemeFromDom() ?? getSystemUiTheme();
  });
  const [uiFont, setUiFontState] = useState<UiFont>(() => {
    if (typeof document === 'undefined') return 'web';
    return getSavedUiFont() ?? getCurrentUiFontFromDom();
  });

  useEffect(() => {
    // Закрываем сайдбар при переходе по роутам (на мобиле)
    setSidebarOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.documentElement.setAttribute('data-ui-sidebar', sidebarOpen ? 'open' : 'closed');
  }, [sidebarOpen]);

  useEffect(() => {
    let isMounted = true;

    const fetchProfileIfNeeded = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        if (isMounted) {
          setUser(null);
          setServiceAccessDenied(false);
        }
        return;
      }

      try {
        const response = await axios.get('/api/auth/profile');
        const userData = readProfileUser(response.data);

        if (userData?.role !== 'admin') {
          let isServiceVisible = false;

          try {
            const sidebarResponse = await axios.get('/api/portal/sidebar-config');
            const services = Array.isArray(sidebarResponse.data?.data?.services)
              ? sidebarResponse.data.data.services
              : [];
            isServiceVisible = services.some((service: any) => service?.slug === 'whitepage-generator');
          } catch {
            // Без подтверждения публикации доступ не даём: безопасный режим.
            isServiceVisible = false;
          }

          if (isMounted) {
            setServiceAccessDenied(!isServiceVisible);
            setUser(isServiceVisible ? userData : null);
          }
          return;
        }

        if (isMounted) {
          setServiceAccessDenied(false);
          setUser(userData);
        }
      } catch {
        if (isMounted) {
          setUser(null);
          setServiceAccessDenied(false);
        }
      }
    };

    fetchProfileIfNeeded();

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'token' || e.key === PORTAL_PROFILE_REVISION_KEY) {
        fetchProfileIfNeeded();
      }
      if (e.key === 'uiTheme') {
        const saved = getSavedUiTheme();
        if (saved) {
          setUiThemeState(saved);
        } else {
          setUiThemeState(getSystemUiTheme());
        }
      }
        if (e.key === 'uiFont') {
          const savedFont = getSavedUiFont();
          if (savedFont) {
            setUiFontState(savedFont);
          } else {
            setUiFontState('web');
          }
        }
    };

    const handleFocus = () => {
      fetchProfileIfNeeded();
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      isMounted = false;
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [location.pathname]);

  useEffect(() => {
    const handlePreviewClick = (event: MouseEvent) => {
      if (!(event.target instanceof Element)) {
        return;
      }

      const anchor = event.target.closest('a');
      if (!anchor) {
        return;
      }

      const href = anchor.getAttribute('href') || '';
      if (!href) {
        return;
      }

      let url: URL;
      try {
        url = new URL(href, window.location.origin);
      } catch {
        return;
      }

      if (!url.pathname.includes('/api/generation/preview-v2/')) {
        return;
      }

      if (event.button !== 0) {
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();

      // Отладочный режим из старой ссылки (?debug=1) уходит в тело /access, а не в query.
      const debug = url.searchParams.get('debug') === '1';
      const generationId = extractPreviewGenerationId(url.pathname);

      // Вкладка открывается синхронно по клику (против блокировщика), opener обнуляется.
      const previewWindow = openPendingPreviewWindow();
      if (!previewWindow) {
        alert('Браузер заблокировал открытие окна превью. Разрешите всплывающие окна.');
        return;
      }

      if (!generationId) {
        navigatePreviewWindow(previewWindow, `${url.origin}${url.pathname}${debug ? '?debug=1' : ''}`);
        return;
      }

      requestPreviewAccess(generationId, { debug })
        .then((previewUrl) => {
          navigatePreviewWindow(previewWindow, previewUrl);
        })
        .catch((error) => {
          console.error('Не удалось открыть превью', error);
          previewWindow.close();
          alert('Не удалось открыть превью. Попробуйте еще раз.');
        });
    };

    document.addEventListener('click', handlePreviewClick, true);

    return () => {
      document.removeEventListener('click', handlePreviewClick, true);
    };
  }, []);

  const pageTitle = useMemo(() => {
    const path = location.pathname;
    if (path === '/dashboard') return 'Дашборд';
    if (path === '/generator-v2') return 'Генератор V2';
    if (path === '/generator') return 'Генератор (legacy)';
    if (path === '/history') return 'История';
    if (path === '/profile') return 'Профиль';
    if (path === '/admin') return 'Админ-панель';
    if (path === '/admin/generator') return 'Тестовая генерация';
    if (path === '/admin-generations') return 'Генерации (админ)';
    if (path === '/admin/billing') return 'Бухгалтерия';
    if (path === '/admin/component-tester') return 'Тестер компонентов';
    if (path === '/admin/background-tester') return 'Тестер фонов';
    if (path === '/admin/services') return 'Сервисы';
    if (path === '/referrals') return 'Реферальная программа';
    return 'Whitepage Generator';
  }, [location.pathname]);

  const isAdmin = user?.role === 'admin';

  // Важно: хуки должны вызываться до любых ранних return.
  const subnavItems = useMemo<Array<{ to: string; label: string; disabled?: boolean; href?: string }>>(() => {
    if (!user) {
      // Вход и регистрация — на странице портала, как у других сервисов.
      return [
        { to: '/', label: 'Главная' },
        { to: 'portal-login', label: 'Войти', href: portalLoginUrl({ returnUrl: dashboardReturnUrl() }) },
        { to: 'portal-register', label: 'Регистрация', href: portalLoginUrl({ register: true, returnUrl: dashboardReturnUrl() }) },
      ];
    }

    return [
      { to: '/dashboard', label: 'Обзор' },
      { to: '/generator-v2', label: 'Создать сайт' },
      { to: '/history', label: 'Мои сайты' },
      { to: '/profile', label: 'Аккаунт портала' },
      ...(isAdmin ? [{ to: '/admin', label: 'Админ-панель' }] : []),
    ];
  }, [isAdmin, user]);

  if (serviceAccessDenied) {
    return (
      <div className="u2-shell">
        <main className="u2-main">
          <div className="u2-container">
            <div className="u2-card">
              <div className="u2-cardInner">
                <h2 className="u2-title">Доступ ограничен</h2>
                <p className="u2-muted">Сервис не опубликован и доступен только администратору.</p>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  const handleLogout = () => {
    // Важно: очищаем и refreshToken, чтобы пользователь не "вернулся" через авто-refresh.
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    window.location.href = '/';
  };

  const handleToggleTheme = () => {
    const current = getCurrentUiThemeFromDom();
    const next: UiTheme = current === 'dark' ? 'light' : 'dark';
    setUiTheme(next);
    setUiThemeState(next);
  };

  const handleToggleFont = () => {
    const current = getCurrentUiFontFromDom();
    const next: UiFont = current === 'system' ? 'web' : 'system';
    setUiFont(next);
    setUiFontState(next);
  };

  return (
    <>
      <div className="u2-backdrop" aria-hidden="true" onClick={() => setSidebarOpen(false)} />

      <div className="u2-shell">
        {/* Единый Web Component <portal-sidebar>: брендинг, список сервисов и user-карточка
            (баланс, пополнить, кабинет, выйти) отрисовываются в shadow DOM из portal-config
            (/api/portal/sidebar.js). Старый AppSidebar.tsx оставлен на диске как «спящий»
            для возможного отката. */}
        <portal-sidebar current-slug={ACTIVE_SERVICE_SLUG} data-theme={uiTheme} />

        <div className="u2-body">
          {/* Единый верхний бар портала */}
          <portal-topbar service-name="Whitepage Generator" data-theme={uiTheme} />


          <div className="u2-subnav" role="navigation" aria-label="Верхнее меню">
            <div className="u2-subnavInner">
              {subnavItems.map((item) => {
                if (item.disabled) {
                  return (
                    <span key={item.to} className="u2-tab is-disabled" aria-disabled="true">
                      {item.label}
                    </span>
                  );
                }
                if (item.href) {
                  return <a key={item.to} className="u2-tab" href={item.href}>{item.label}</a>;
                }
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    className={({ isActive }) => `u2-tab ${isActive ? 'is-active' : ''} ${item.to === '/generator-v2' ? 'is-createSite' : ''}`}
                  >
                    {item.label}
                  </NavLink>
                );
              })}
          <AppTopbar
            title="Whitepage Generator"
            subtitle={`Портал сервисов • ${pageTitle}`}
            user={user}
            uiTheme={uiTheme}
            uiFont={uiFont}
            onToggleFont={handleToggleFont}
            onToggleTheme={handleToggleTheme}
            onToggleSidebar={() => setSidebarOpen((prev) => !prev)}
            onLogout={handleLogout}
          />
            </div>
          </div>

          <main className="u2-main">
            <div className="u2-container">
              {isAdmin && (location.pathname === '/admin' || location.pathname.startsWith('/admin/') || location.pathname === '/admin-generations') && <AdminNavigation />}
              <Outlet />
            </div>
          </main>
        </div>
      </div>
    </>
  );
};

export default AppLayout;
